import { useAction, useAtom } from '@reatom/npm-react'
import * as DateF from 'date-fns'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { EditIcon, Typography } from '@blade/ui-kit'
import { Numeric } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { nativePriceAtom } from '@root/features/realtime/atoms'
import { environmentAtom } from '@root/shared'
import { useDateFnsLocale } from '@root/shared/localization'

import { type TransferSvmFeeReatom } from '../../../atoms/transfer-svm-fee-reatom'

import type { ClassNameProps } from '@root/shared/react'

type TransferFeeCardProps = {
  variant?: 'dark' | 'normal'
  model: TransferSvmFeeReatom
} & ClassNameProps

export const TransferFeeCardSvm: React.FC<TransferFeeCardProps> = ({
  className,
  variant = 'normal',
  model,
}) => {
  const { t } = useTranslation()
  const locale = useDateFnsLocale()

  const [nativePrice] = useAtom(nativePriceAtom)
  const [settings] = useAtom(model.svmFeeSettingsAtom)
  const [{ transferFeeSvm }] = useAtom(environmentAtom)

  const openSettings = useAction(model.changeAction)

  const priorityFee = settings.jitoEnabled
    ? settings.jitoTip
    : settings.priorityFee

  return (
    <Root className={className} $variant={variant} onClick={openSettings}>
      <FeeContainer>
        <Column>
          <Label>{t('trade_settings.transaction-fee')}:</Label>
          <Label>
            {settings.jitoEnabled
              ? t('trade_settings.jito_tip')
              : t('trade_settings.priority_fee')}
            :
          </Label>
        </Column>
        <Column>
          <Value>
            <Numeric
              value={transferFeeSvm}
              preset="currency-amount-short"
              currency={CurrencyModel.Sol.ticker}
            />
          </Value>
          <EditableValue>
            <EditButton>
              <EditIcon size={12} />
            </EditButton>

            <Numeric
              preset="currency-amount-short"
              value={priorityFee}
              currency={CurrencyModel.Sol.ticker}
            />
          </EditableValue>
        </Column>
        <Column>
          <ValueSecondary>
            <Numeric
              preset="fee-usd"
              value={nativePrice.multipliedBy(transferFeeSvm)}
              currency="$"
            />
          </ValueSecondary>
          <ValueSecondary>
            <Numeric
              preset="fee-usd"
              value={nativePrice.multipliedBy(priorityFee)}
              currency="$"
            />
          </ValueSecondary>
        </Column>
      </FeeContainer>
      <Container>
        <WaitTimeLabel>{t('trading.parameters.avg_wait_time')}</WaitTimeLabel>
        <WaitTimeValue>
          ~{DateF.formatDuration({ seconds: 10 }, { locale })}
        </WaitTimeValue>
      </Container>
    </Root>
  )
}

const Root = styled.div<{ $variant: 'normal' | 'dark' }>`
  cursor: pointer;
  padding-block: 12px;
  padding-inline: 4px 4px;

  border-radius: 16px;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'dark'
        ? `rgba(var(--background-12-rgb), 0.05)`
        : 'var(--grayA-2)'};
  background: ${({ $variant }) =>
    $variant === 'dark' ? 'var(--background-1)' : 'var(--backgroundA-3)'};

  display: grid;
  align-items: center;
  grid-template-columns: auto auto;
`

const EditButton = styled.div`
  display: grid;
  margin-inline: 0 2px;
  color: var(--amber-10);
`

const Column = styled.div`
  display: grid;
  align-items: center;
  text-align: right;

  &:first-of-type {
    text-align: left;
  }
`

const Container = styled.div`
  display: grid;
  gap: 2px;

  padding-inline: 8px;
`

const FeeContainer = styled(Container)`
  border-right: 1px solid var(--backgroundA-4);
  grid-template-columns: auto 1fr auto;
  gap: 6px;
`

const Label = styled(Typography).attrs({
  variant: 'body4-semi-bold',
  component: 'div',
})`
  color: var(--text-9);

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const WaitTimeLabel = styled(Typography).attrs({
  variant: 'body4-medium',
  component: 'div',
})`
  color: var(--text-11);
  text-align: center;

  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const Value = styled(Typography).attrs({
  variant: 'body2-semi-bold',
  component: 'div',
})`
  color: var(--text-12);
`

const EditableValue = styled(Value)`
  display: grid;
  grid-auto-flow: column;
  align-items: center;
  justify-content: end;
  gap: 2px;
  color: var(--amber-10);
`

const WaitTimeValue = styled(Typography).attrs({
  variant: 'body2-semi-bold',
  component: 'div',
})`
  color: var(--text-12);
  text-align: center;
`

const ValueSecondary = styled(Typography).attrs({
  variant: 'body2-medium',
  component: 'div',
})`
  color: var(--text-9);
`
